<script setup lang="ts">
import {ref} from "vue";
import {useRouter} from "vue-router";
import AgendaAppointmentDetailModal from "@/agenda/components/AgendaAppointmentDetailModal.vue";
import AgendaAppointmentFormModal from "@/agenda/components/AgendaAppointmentFormModal.vue";
import AgendaRescheduleDialog from "@/agenda/components/AgendaRescheduleDialog.vue";
import useAppointmentMutations from "@/agenda/composables/useAppointmentMutations";
import useOrganizationId from "@/core/composables/useOrganizationId";
import useHealthcenterOpening from "@/healthcenter/composables/useHealthcenterOpening";
import {AgendaAppointment} from "@/core/interfaces/AgendaAppointment";
import {AppointmentCreateRequest} from "@/core/http/resources/AppointmentResource";
import {parseAgendaDate} from "@/agenda/lib/agendaTime";
import {PatientSmsChoice} from "@/agenda/lib/patientSms";

/**
 * An appointment's detail and what can be done from it — cancel, move, edit —
 * the same from the calendar as from the patient file. The parent picks the
 * appointment and refetches on `changed`.
 */
const props = defineProps<{
    appointment: AgendaAppointment | null;
}>();

const emit = defineEmits<{
    /** The appointment was cancelled, moved or edited. */
    (event: 'changed'): void
}>();

const open = defineModel<boolean>('open', {required: true});

const router = useRouter();

const organizationId = useOrganizationId();

const {opening} = useHealthcenterOpening();

const {saving, edit, reschedule, cancel} = useAppointmentMutations();

const rescheduleOpen = ref(false);

const formOpen = ref(false);

/** One dialog at a time: the detail gives way to the one it opens. */
const openReschedule = () => {
    open.value = false;
    rescheduleOpen.value = true;
};

const openForm = () => {
    open.value = false;
    formOpen.value = true;
};

/**
 * Leaving for the patient's file, or their beneficiary's: the detail closes
 * first so it does not linger over the file.
 */
const openPatient = (customerId: number, beneficiaryId?: number) => {
    open.value = false;
    router.push(beneficiaryId
        ? {name: 'customer.beneficiary', params: {customerId, id: beneficiaryId}}
        : {name: 'customer.home', params: {customerId}}
    );
};

const handleCancel = (sms: PatientSmsChoice) => {
    if (props.appointment) {
        cancel(props.appointment.id, sms, () => {
            open.value = false;
            emit('changed');
        });
    }
};

const handleReschedule = (sms: PatientSmsChoice, start: Date) => {
    if (props.appointment) {
        reschedule(props.appointment.id, parseAgendaDate(start).format(), sms, () => {
            rescheduleOpen.value = false;
            emit('changed');
        });
    }
};

const handleSubmit = (request: AppointmentCreateRequest, sms: PatientSmsChoice | null) => {
    if (props.appointment && organizationId.value) {
        edit(organizationId.value, props.appointment.id, request, sms, () => {
            formOpen.value = false;
            emit('changed');
        });
    }
};
</script>

<template>
  <AgendaAppointmentDetailModal
    v-model:open="open"
    :appointment="appointment"
    :loading="saving"
    @edit="openForm"
    @reschedule="openReschedule"
    @cancel-appointment="handleCancel"
    @open-patient="openPatient"
  />
  <AgendaRescheduleDialog
    v-model:open="rescheduleOpen"
    :appointment="appointment"
    :opening="opening"
    :loading="saving"
    @confirm="handleReschedule"
  />
  <AgendaAppointmentFormModal
    v-model:open="formOpen"
    :appointment="appointment"
    :opening="opening"
    :loading="saving"
    @submit="handleSubmit"
  />
</template>
